import React from "react";

const colorPalette = [
  {
    name: "Electrum (Brand Blue)",
    swatches: [
      {
        className: "bg-electrum-100",
        hex: "#CEEDF9",
        name: "electrum-100",
        usage: "Button backgrounds, highlights, tab backgrounds",
      },
      {
        className: "bg-electrum-150",
        hex: "#A1DFF7",
        name: "electrum-150",
        usage: "Button hover, tab hover",
      },
      {
        className: "bg-electrum-200",
        hex: "#8ED6ED",
        name: "electrum-200",
        usage: "Borders, subtle highlights",
      },
      {
        className: "bg-electrum-250",
        hex: "#38B2E0",
        name: "electrum-250",
        usage: "Accent, charts",
      },
      {
        className: "bg-electrum-300",
        hex: "#00A3E0",
        name: "electrum-300",
        usage: "Tab active, analytics numbers",
      },
      {
        className: "bg-electrum-400 bg-electrum",
        hex: "#115088",
        name: "electrum-400/electrum",
        usage: "Primary text, links, brand, button text",
      },
    ],
  },
  {
    name: "Salmon (Caution/Destructive)",
    swatches: [
      {
        className: "bg-salmon-100",
        hex: "#FFEBEF",
        name: "salmon-100",
        usage: "Backgrounds, subtle error highlights",
      },
      {
        className: "bg-salmon-200",
        hex: "#FFA6A6",
        name: "salmon-200",
        usage: "Button hover, error highlights",
      },
      {
        className: "bg-salmon-300",
        hex: "#E2395B",
        name: "salmon-300",
        usage: "Cautionary button backgrounds, error text",
      },
      {
        className: "bg-salmon-400",
        hex: "#840000",
        name: "salmon-400",
        usage: "Destructive/caution text, error emphasis",
      },
    ],
  },
  {
    name: "Gray (UI/Neutral)",
    swatches: [
      {
        className: "bg-gray-100",
        hex: "#f5f5f5",
        name: "gray-100",
        usage: "Backgrounds, cards, containers",
      },
      {
        className: "bg-gray",
        hex: "#C4C4C4",
        name: "gray",
        usage: "Disabled text, icons, backgrounds",
      },
      {
        className: "bg-gray-focus",
        hex: "#808080",
        name: "gray-focus",
        usage: "Focus/hover states",
      },
      {
        className: "bg-gray-hover",
        hex: "#808080",
        name: "gray-hover",
        usage: "Hover states",
      },
    ],
  },
];

export default function Typography() {
  return (
    <div className="space-y-10">
      {/* Overview */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Typography System
        </h2>
        <p className="text-gray-700 mb-2">
          The Psych For Life app uses{" "}
          <span className="font-bold">Museo Slab</span> as its primary font
          family for a friendly, readable, and distinctive look. All text is
          styled with Tailwind CSS utility classes and custom theme colors.
        </p>
        <p className="text-gray-600 text-sm">
          Font family:{" "}
          <span className="font-mono">&apos;museo-slab&apos;, sans-serif</span>{" "}
          (set globally via Tailwind and MUI)
        </p>
      </div>

      {/* Usage Guidelines */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Usage Guidelines
        </h3>
        <ul className="space-y-2 text-sm text-gray-800">
          <li>
            • Use semantic HTML elements (h1-h6, p, span) for accessibility and
            SEO
          </li>
          <li>
            • <span className="font-bold text-gray-900">One h1 per page</span>{" "}
            (e.g., page title)
          </li>
          <li>
            • Use <span className="font-bold text-electrum">text-electrum</span>{" "}
            for primary actions, links, and highlights
          </li>
          <li>
            • Use{" "}
            <span className="font-bold text-salmon-400">text-salmon-400</span>{" "}
            for cautionary/destructive actions and error states
          </li>
          <li>
            • Use <span className="font-bold text-gray-900">text-gray-900</span>{" "}
            for headings,{" "}
            <span className="font-bold text-gray-800">text-gray-800/700</span>{" "}
            for body, and{" "}
            <span className="font-bold text-gray-600">text-gray-600/500</span>{" "}
            for muted/secondary text
          </li>
          <li>
            • Responsive typography: use{" "}
            <span className="font-mono text-gray-700">
              text-sm md:text-base lg:text-lg
            </span>{" "}
            for scaling
          </li>
        </ul>
      </div>

      {/* Color Palette */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Color Palette
        </h3>
        <div className="space-y-6">
          {colorPalette.map((group) => (
            <div key={group.name}>
              <div className="font-bold mb-3">{group.name}</div>
              <div className="space-y-2">
                {group.swatches.map((swatch) => (
                  <div
                    key={swatch.className}
                    className="flex items-center gap-3"
                  >
                    <span
                      className={`inline-block w-8 h-8 rounded border ${swatch.className}`}
                    />
                    <span className="font-mono text-sm">{swatch.hex}</span>
                    <span className="font-mono text-sm text-gray-600">
                      {swatch.name}
                    </span>
                    <span className="text-sm text-gray-700">
                      {swatch.usage}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Font Sizes in Use */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Font Sizes in Use
        </h3>
        <div className="space-y-6">
          {/* Large Headings */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Large Headings (text-4xl, text-5xl)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-blue-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  LandingPage Hero Title
                </p>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">
                  Psych For Life®
                </h1>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-4xl sm:text-5xl font-bold text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-blue-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  ComponentLibraryPage Header
                </p>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Design System
                </h1>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-3xl font-bold text-gray-900
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Medium Headings */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Medium Headings (text-2xl, text-3xl)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  LandingPage Section Headings
                </p>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Navigate Life&apos;s Challenges with Confidence
                </h2>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-3xl font-bold text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  LandingPage Subsection Headings
                </p>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Why Choose Psych For Life®?
                </h3>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-2xl font-semibold text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  LessonPage Completion Message
                </p>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Congratulations!
                </h1>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-2xl font-bold text-gray-900
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Small Headings */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Small Headings (text-xl, text-lg)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  LandingPage Benefit Cards
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Build Existing Strengths
                </h3>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-xl font-semibold text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-yellow-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  AnalyticsWidget Titles
                </p>
                <h3 className="text-lg text-gray-700 mb-2">
                  Analytics Dashboard
                </h3>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-lg text-gray-700
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-yellow-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  Card Component Titles
                </p>
                <h4 className="text-base font-semibold text-gray-900 mb-2">
                  Card Title
                </h4>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-md font-semibold
                  </code>{" "}
                  (note: should be text-base)
                </p>
              </div>
            </div>
          </div>

          {/* Body Text */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Body Text (text-base, text-lg)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-purple-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  LandingPage Hero Subtitle
                </p>
                <p className="text-xl sm:text-2xl text-gray-800 mb-2">
                  Science-backed tools to help you navigate life&apos;s
                  challenges with confidence
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-xl sm:text-2xl text-gray-800
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-purple-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  LandingPage Beta Text
                </p>
                <p className="text-lg text-gray-600 mb-2">
                  Beta version launching Spring &apos;25!
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-lg text-gray-600
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-purple-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  TutorialPage Descriptions
                </p>
                <p className="text-sm md:text-base text-gray-700 mb-2">
                  Tutorial description text with responsive sizing
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-sm md:text-base text-gray-700
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Small Text */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Small Text (text-sm, text-xs)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-red-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  AnalyticsWidget Subtitles
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  Analytics subtitle text
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-sm text-gray-600
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-red-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">ViewLogModal Dates</p>
                <p className="text-sm text-gray-600 mb-2">January 15, 2025</p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-sm text-gray-600
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-red-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  KeyTakeaways Labels
                </p>
                <p className="text-sm font-medium text-gray-900 mb-2">
                  Question label text
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-sm font-medium
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Special Cases */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Special Cases
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-indigo-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Analytics Numbers</p>
                <p className="text-3xl font-bold text-electrum-300 mb-2">
                  1,234
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-3xl font-bold text-electrum-300
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-indigo-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  Button Text (Large)
                </p>
                <p className="text-lg font-bold text-electrum mb-2">
                  Learn More
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-lg font-bold
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-indigo-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  ModuleButton (Tall Variant)
                </p>
                <p className="text-sm font-normal text-black mb-2">
                  Module text
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-sm font-normal
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Font Weights in Use */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Font Weights in Use
        </h3>
        <div className="space-y-6">
          {/* Bold */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Bold (font-bold)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-blue-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  Page Titles & Main Headings
                </p>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Psych For Life®
                </h1>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-3xl font-bold text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-blue-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Button Text</p>
                <p className="text-lg font-bold text-electrum mb-2">
                  Learn More
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-lg font-bold
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-blue-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Analytics Numbers</p>
                <p className="text-3xl font-bold text-electrum-300 mb-2">
                  1,234
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-3xl font-bold text-electrum-300
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Semibold */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Semibold (font-semibold)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Section Headings</p>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Why Choose Psych For Life®?
                </h3>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-2xl font-semibold text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Card Titles</p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Build Existing Strengths
                </h3>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-xl font-semibold text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Form Labels</p>
                <p className="text-base font-semibold text-gray-900 mb-2">
                  Username
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-base font-semibold text-gray-900
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Medium */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Medium (font-medium)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  KeyTakeaways Labels
                </p>
                <p className="text-sm font-medium text-gray-900 mb-2">
                  Question label text
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-sm font-medium
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-yellow-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">CardButton Text</p>
                <p className="text-base font-medium text-black mb-2">
                  Card button text
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-base font-medium text-black
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Normal */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Normal (font-normal)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-purple-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  ModuleButton (Tall Variant)
                </p>
                <p className="text-sm font-normal text-black mb-2">
                  Module text
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-sm font-normal
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-purple-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Body Text</p>
                <p className="text-base font-normal text-gray-800 mb-2">
                  Default body text for most content areas
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-base font-normal text-gray-800
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Line Heights in Use */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Line Heights in Use
        </h3>
        <div className="space-y-6">
          {/* Tight */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Tight (leading-tight)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-blue-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  Card Titles & Short Headings
                </p>
                <h3 className="text-xl font-semibold leading-tight text-gray-900 mb-2">
                  Build Existing Strengths
                </h3>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-xl font-semibold leading-tight text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-blue-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">
                  Compact Text Blocks
                </p>
                <p className="text-base leading-tight text-gray-900 mb-2">
                  Short text blocks where you want to reduce vertical spacing
                  between lines for a more compact appearance.
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-base leading-tight text-gray-900
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Normal */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Normal (leading-normal)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Default Body Text</p>
                <p className="text-base leading-normal text-gray-900 mb-2">
                  Default line height used for most body text and content.
                  Provides good readability for standard paragraph length and
                  general content.
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-base leading-normal text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-green-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Form Content</p>
                <p className="text-base leading-normal text-gray-800 mb-2">
                  Form descriptions and input content use normal line height for
                  optimal readability.
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-base leading-normal text-gray-800
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Relaxed */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Relaxed (leading-relaxed)
            </h4>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Longer Paragraphs</p>
                <p className="text-base leading-relaxed text-gray-900 mb-2">
                  Used for longer paragraphs and content-heavy sections where
                  you want more breathing room between lines. Great for
                  improving readability in longer content blocks and detailed
                  explanations.
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-base leading-relaxed text-gray-900
                  </code>
                </p>
              </div>
              <div className="border-l-4 border-yellow-200 pl-4">
                <p className="text-sm text-gray-600 mb-1">Hero Content</p>
                <p className="text-lg leading-relaxed text-gray-800 mb-2">
                  Hero sections and important content areas use relaxed line
                  height for better visual impact and readability.
                </p>
                <p className="text-xs text-gray-500">
                  Classes:{" "}
                  <code className="bg-gray-100 px-1 rounded">
                    text-lg leading-relaxed text-gray-800
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Line Widths */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Line Widths
        </h3>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">Narrow (max-w-sm)</p>
            <div className="max-w-sm bg-gray-100 p-3 rounded">
              <p className="text-base text-gray-900">
                Used for short labels, captions, and compact content areas like
                form fields and small cards.
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Medium (max-w-md)</p>
            <div className="max-w-md bg-gray-100 p-3 rounded">
              <p className="text-base text-gray-900">
                Used for standard content blocks, modal content, and
                medium-sized cards. Good for readable paragraph length.
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Wide (max-w-lg)</p>
            <div className="max-w-lg bg-gray-100 p-3 rounded">
              <p className="text-base text-gray-900">
                Used for wider content areas, main content sections, and larger
                cards. Provides more breathing room for longer text content.
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Full Width (no max-width)
            </p>
            <div className="bg-gray-100 p-3 rounded">
              <p className="text-base text-gray-900">
                Used for full-width content areas, hero sections, and when
                content should span the entire available width.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Text Alignment */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Text Alignment
        </h3>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Left Aligned (text-left)
            </p>
            <p className="text-base text-left text-gray-900 border-l-4 border-gray-200 pl-4">
              Default for most content (e.g., paragraphs, lists)
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Center Aligned (text-center)
            </p>
            <p className="text-base text-center text-gray-900 border-l-4 border-gray-200 pl-4">
              Used for headings, titles, and call-to-action elements
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Right Aligned (text-right)
            </p>
            <p className="text-base text-right text-gray-900 border-l-4 border-gray-200 pl-4">
              Used for numbers, dates, and certain UI elements
            </p>
          </div>
        </div>
      </div>

      {/* Responsive Typography */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Responsive Typography
        </h3>
        <p className="text-sm text-gray-700 mb-3">
          Typography scales across devices using Tailwind&apos;s responsive
          classes. Example:{" "}
          <code className="bg-gray-100 px-1 rounded">
            text-sm md:text-base lg:text-lg
          </code>
        </p>
        <div className="space-y-2 text-sm text-gray-700">
          <p>• Mobile: Smaller font sizes for fit</p>
          <p>• Tablet: Medium font sizes for readability</p>
          <p>• Desktop: Larger font sizes for optimal reading</p>
        </div>
      </div>
    </div>
  );
}
